import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { MapPin, Search } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { useApp } from "@/context/AppContext";
import {
  fetchCardPlacements,
  type CardPlacement,
} from "@/context/inventorySync";
import { containerPath, formatContainerPath } from "@/features/containerPath";

export const Route = createFileRoute("/tools/locate")({
  validateSearch: (s: Record<string, unknown>): { card?: string } => ({
    card: typeof s.card === "string" ? s.card : undefined,
  }),
  component: LocatePage,
});

/**
 * "Where is this card?" lookup (TNK-189). Search inventory by name or scan a
 * `set number`, then see every container it's placed in — full path + quantity.
 * Read-only; the counterpart to chaos intake (TNK-188), which is otherwise
 * write-only. Mobile-first: the operator uses this standing at the shelves.
 */
function LocatePage() {
  const { card: cardParam } = Route.useSearch();
  const { cards, containers } = useApp();

  const [input, setInput] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Per-page placement cache: undefined = not loaded, "loading"/"error", or rows.
  const [pmap, setPmap] = useState<
    Record<string, CardPlacement[] | "loading" | "error">
  >({});

  const byId = useMemo(
    () => new Map(containers.map((c) => [c.id, c])),
    [containers],
  );

  const results = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (!q) return [];
    const tokens = q.split(/\s+/);
    return cards
      .filter((c) => {
        if (c.archivedAt) return false; // archived = no physical copy to find
        const hay =
          `${c.name} ${c.setName} ${c.setCode} ${c.cardNumber}`.toLowerCase();
        return tokens.every((t) => hay.includes(t));
      })
      .slice(0, 25);
  }, [cards, input]);

  async function loadPlacements(cardId: string) {
    setPmap((prev) => ({ ...prev, [cardId]: "loading" }));
    try {
      const rows = await fetchCardPlacements(cardId);
      setPmap((prev) => ({ ...prev, [cardId]: rows }));
    } catch {
      setPmap((prev) => ({ ...prev, [cardId]: "error" }));
    }
  }

  function selectCard(cardId: string) {
    setSelectedId((cur) => (cur === cardId ? null : cardId));
    if (pmap[cardId] === undefined) void loadPlacements(cardId);
  }

  // Deep-link: /tools/locate?card=<id> preselects + prefills the search so the
  // card shows in results (entry point from the inventory detail).
  useEffect(() => {
    if (!cardParam) return;
    const card = cards.find((c) => c.id === cardParam);
    if (!card) return;
    setInput(card.name);
    setSelectedId(cardParam);
    if (pmap[cardParam] === undefined) void loadPlacements(cardParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardParam, cards]);

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/tools" }} label="Tools" />
      </div>

      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">Find a Card</h1>
        <p className="text-sm text-muted-foreground">
          Search by name or scan <span className="font-mono">set number</span>{" "}
          to see exactly which container(s) hold it.
        </p>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto py-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoFocus
            autoComplete="off"
            placeholder="Card name, set, number…"
            className="w-full rounded-md border border-border bg-card py-2.5 pl-9 pr-3 text-base text-foreground outline-none focus:border-primary"
          />
        </div>

        {input.trim() === "" ? (
          <div className="rounded-md border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
            Start typing to find where a card lives.
          </div>
        ) : results.length === 0 ? (
          <div className="rounded-md border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
            No in-stock cards match “{input.trim()}”.
          </div>
        ) : (
          <div className="space-y-1">
            {results.map((c) => {
              const expanded = selectedId === c.id;
              const ps = pmap[c.id];
              return (
                <div
                  key={c.id}
                  className="rounded-md border border-border bg-card"
                >
                  <button
                    type="button"
                    onClick={() => selectCard(c.id)}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted/50"
                  >
                    {c.imageFrontSourceUrl ? (
                      <img
                        src={c.imageFrontSourceUrl}
                        alt=""
                        className="h-10 w-7 shrink-0 rounded object-cover"
                      />
                    ) : (
                      <div className="h-10 w-7 shrink-0 rounded bg-muted" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">
                        {c.name}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {c.setName} {c.cardNumber ? `#${c.cardNumber}` : ""} ·{" "}
                        {c.condition} · qty {c.quantity}
                      </div>
                    </div>
                  </button>

                  {expanded && (
                    <div className="space-y-1 border-t border-border px-3 py-2">
                      {ps === "loading" || ps === undefined ? (
                        <div className="text-xs text-muted-foreground">
                          Locating…
                        </div>
                      ) : ps === "error" ? (
                        <div className="text-xs text-destructive">
                          Couldn’t load placements. Refresh and retry.
                        </div>
                      ) : ps.length === 0 ? (
                        <div className="text-xs text-muted-foreground">
                          {c.storageLocation
                            ? `Bin “${c.storageLocation}” (legacy — not in the placement model yet)`
                            : "Not placed in any container yet."}
                        </div>
                      ) : (
                        ps.map((p) => {
                          const path = containerPath(p.containerId, byId);
                          const node = byId.get(p.containerId);
                          const label = path.length
                            ? formatContainerPath(path)
                            : p.containerId;
                          return (
                            <div
                              key={p.containerId}
                              className="flex items-center gap-2 text-sm text-foreground"
                            >
                              <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
                              <span className="min-w-0 flex-1 truncate">
                                {label}
                                {node?.archivedAt && (
                                  <span className="ml-1.5 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-700">
                                    archived
                                  </span>
                                )}
                              </span>
                              <span className="shrink-0 text-xs font-semibold text-muted-foreground">
                                ×{p.quantity}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
